/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import Title from '@/components/AZCustomComponent/Title';
import { useLabels } from '@/hooks/useLabels';
import { SearchBar } from '@az/starc-ui';
import styles from './styles.module.scss';
import { useState } from 'react';
import dynamic from 'next/dynamic';
import { Formik, Form } from 'formik';
import { validateSearch } from '@/components/StoreLocator';
import { type ContentStackStoreLocator } from '@/features/contentstack/interface/content-types';
import { useLocale } from '@/hooks/useLocale';
import { interpolateLabel } from '@/features/i18n/utils/interpolateLabel';

const LazySearchStoreModal = dynamic(() =>
  import('../StoreModal/SearchStoreModal').then((module) => ({
    default: module.SearchStoreModal,
  }))
);

const labelMap = {
  input: 'label_storeLocator_fieldPlaceholder',
  lblRequired: 'label_StoreLocator_modal_Required',
  lblPlease_remove_invalid_characters: 'label_Please_remove_invalid_characters',
  lblZipCodeMustBe5Digits: 'error_RewardsSignUp_body_ZipCodeMustBe5Digits',
  lblZipCodeBrazil: 'error_Brazil_ZipCodeFormat',
  locationError: 'label_location_error',
};

export function SearchStore({
  content,
  make,
}: {
  content: ContentStackStoreLocator;
  make?: string | null;
}) {
  const labels = useLabels(labelMap);
  const locale = useLocale();
  const [showStoreLocator, setShowStoreLocator] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const title = make ? interpolateLabel(content.heading, { make }) : content.heading;

  const validate = (values: { searchValue: string }) => {
    const error = validateSearch(values.searchValue, labels, locale, {
      isUserCurrentLocation: false,
      hasData: false,
    });
    if (error) {
      return { searchValue: error };
    }
    return {};
  };

  const handleClose = () => {
    setShowStoreLocator(false);
    setSearchValue('');
  };

  return (
    <div>
      <div className={styles.searchStore}>
        <Title stylesClass={styles.title} firstText={title} underline variant="md" />
        <Formik
          initialValues={{ searchValue: '' }}
          validate={validate}
          onSubmit={(values) => {
            setSearchValue(values.searchValue);
            setShowStoreLocator(true);
          }}
          validateOnChange={false}
          validateOnBlur={true}
        >
          {({ values, errors, handleChange }) => (
            <Form>
              <SearchBar
                inputContainerClassName={styles.searchBarInputContainer}
                onValueChange={(value: string) => {
                  handleChange({
                    target: {
                      name: 'searchValue',
                      value,
                    },
                  });
                }}
                hideSuggestions
                value={values.searchValue}
                label={labels.input}
              />
              {errors.searchValue && (
                <p role="alert" className={styles.errorMessage}>
                  {errors.searchValue}
                </p>
              )}
            </Form>
          )}
        </Formik>

        <p className={styles.description}>{content.subtitle}</p>
      </div>

      {showStoreLocator && (
        <LazySearchStoreModal searchValue={searchValue} closeStoreModal={handleClose} />
      )}
    </div>
  );
}
